import React, { useState, useRef, useEffect } from 'react';
import { 
  Brain, CloudRain, Heart, Lightbulb, Smile, MessageSquare, Loader, 
  PenSquare, AlertTriangle, X, HelpCircle, Weight, Angry, 
  SunMedium, HeartCrack, Frown, CircleDot, Users2, Home, 
  Church, Skull, UserMinus, Users, Compass, BookOpen, 
  Shield, ArrowDown, Footprints, Star, CircleUser,
  Clock, Bookmark, FileQuestion, Glasses, UserCog, Crown
} from 'lucide-react';
import { BibleAI } from '../lib/ai';
import { InlineAIAssistant } from '../components/InlineAIAssistant';
import { NoteEditor } from '../components/NoteEditor';
import { MoodResponseModal } from '../components/MoodResponseModal';
import { useNavigationStore } from '../stores/navigationStore';
import { parseReference } from '../utils/parseReference';
import { useNavigate, useLocation } from 'react-router-dom';
import { HelpButton } from '../components/common/HelpButton';
import { ProFeatureGuard } from '../components/auth/ProFeatureGuard';

const moodCategories = [
  {
    category: "Emotional Struggles",
    moods: [
      { id: 'anxious', label: 'Anxious', icon: Brain },
      { id: 'sad', label: 'Sad', icon: CloudRain },
      { id: 'angry', label: 'Angry', icon: Angry },
      { id: 'doubtful', label: 'Doubtful', icon: HelpCircle },
      { id: 'overwhelmed', label: 'Overwhelmed', icon: Weight },
      { id: 'lonely', label: 'Lonely', icon: UserMinus },
      { id: 'jealous', label: 'Jealous', icon: HeartCrack },
      { id: 'weary', label: 'Weary', icon: Frown },
      { id: 'fearful', label: 'Fearful', icon: Shield },
      { id: 'stressed', label: 'Stressed', icon: Weight },
      { id: 'discouraged', label: 'Discouraged', icon: ArrowDown },
      { id: 'ashamed', label: 'Ashamed', icon: UserMinus },
      { id: 'self-esteem', label: 'Low Self-Esteem', icon: Crown }
    ]
  },
  {
    category: "Positive States",
    moods: [
      { id: 'grateful', label: 'Grateful', icon: Heart },
      { id: 'hopeful', label: 'Hopeful', icon: Smile },
      { id: 'uplifting', label: 'Uplifting', icon: SunMedium },
      { id: 'reflective', label: 'Reflective', icon: CircleDot },
      { id: 'seeking', label: 'Seeking Wisdom', icon: Lightbulb },
      { id: 'joyful', label: 'Joyful', icon: Star },
      { id: 'peaceful', label: 'Peaceful', icon: CircleDot },
      { id: 'motivated', label: 'Motivated', icon: Lightbulb },
      { id: 'faithful', label: 'Faithful', icon: Heart }
    ]
  },
  {
    category: "Life Situations",
    moods: [
      { id: 'marriage', label: 'Marriage', icon: Users2 },
      { id: 'parenting', label: 'Parenting', icon: Home },
      { id: 'grieving', label: 'Grieving', icon: CloudRain },
      { id: 'career', label: 'Career Guidance', icon: Compass },
      { id: 'decisions', label: 'Decision Making', icon: FileQuestion },
      { id: 'waiting', label: 'Waiting Season', icon: Clock },
      { id: 'transition', label: 'Life Transition', icon: Footprints }
    ]
  },
  {
    category: "Spiritual Growth",
    moods: [
      { id: 'worship', label: 'Worship', icon: Heart },
      { id: 'studying', label: 'Bible Study', icon: BookOpen },
      { id: 'discipleship', label: 'Discipleship', icon: Footprints },
      { id: 'purpose', label: 'Finding Purpose', icon: Compass },
      { id: 'wisdom', label: 'Divine Wisdom', icon: Bookmark },
      { id: 'revival', label: 'Spiritual Revival', icon: Star }
    ]
  },
  {
    category: "Spiritual Challenges",
    moods: [
      { id: 'idolatry', label: 'Idolatry', icon: Church },
      { id: 'sinful', label: 'Sinful', icon: Skull },
      { id: 'spiteful', label: 'Spiteful', icon: Users },
      { id: 'tempted', label: 'Temptation', icon: Shield },
      { id: 'lukewarm', label: 'Lukewarm Faith', icon: CircleUser },
      { id: 'discerning', label: 'Need Discernment', icon: Glasses }
    ]
  },
  {
    category: "Relationship Healing",
    moods: [
      { id: 'forgiveness', label: 'Seeking Forgiveness', icon: Heart },
      { id: 'reconciliation', label: 'Reconciliation', icon: Users2 },
      { id: 'boundaries', label: 'Setting Boundaries', icon: Shield },
      { id: 'trust', label: 'Building Trust', icon: UserCog },
      { id: 'conflict', label: 'Resolving Conflict', icon: Users }
    ]
  }
];

export default function MoodScriptures() {
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [situation, setSituation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verses, setVerses] = useState<Array<{
    reference: string;
    text: string;
    reason: string;
  }> | null>(null);
  const [showAI, setShowAI] = useState(false);
  const [aiQuestion, setAiQuestion] = useState<string | null>(null);
  const [selectedVerse, setSelectedVerse] = useState<{
    reference: string;
    text: string;
  } | null>(null);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [verseForNote, setVerseForNote] = useState<{
    id: string;
    reference: string;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigationStore = useNavigationStore();
  const navigate = useNavigate();
  const location = useLocation();

  const aiRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showAI && aiRef.current) {
      aiRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showAI]);

  const findScriptures = async (mood: string, currentSituation: string = '') => {
    setLoading(true);
    setError(null);
    setVerses(null);
    setShowAI(false);
    setAiQuestion(null);
    setSelectedVerse(null);
    setIsModalOpen(true);

    try {
      const ai = await BibleAI.getInstance();
      const foundVerses = await ai.findScripturesForMood(mood, currentSituation);
      if (!foundVerses || foundVerses.length === 0) {
        throw new Error('No scriptures found. Please try again.');
      }
      setVerses(foundVerses);
    } catch (error) {
      console.error('Error finding scriptures:', error);
      setError(error instanceof Error ? error.message : 'Failed to find scriptures');
    } finally {
      setLoading(false);
    }
  };

  const handleMoodSelect = async (mood: string) => {
    setSelectedMood(mood);
    await findScriptures(mood, situation);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMood(null);
    setVerses(null);
    setError(null);
  };

  const handleSituationKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && situation.trim()) {
      e.preventDefault();
      setLoading(true);
      setError(null);
      setVerses(null);
      setShowAI(false);
      setAiQuestion(null);
      setSelectedVerse(null);
      setIsModalOpen(true);

      try {
        const ai = await BibleAI.getInstance();
        const foundVerses = await ai.findScripturesForSituation(situation.trim());
        if (!foundVerses || foundVerses.length === 0) {
          throw new Error('No scriptures found. Please try again.');
        }
        setVerses(foundVerses);
      } catch (error) {
        console.error('Error finding scriptures:', error);
        setError(error instanceof Error ? error.message : 'Failed to find scriptures');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleVerseClick = (reference: string) => {
    const { book, chapter, verse } = parseReference(reference);
    if (book && chapter) {
      navigationStore.setPreviousLocation({
        pathname: location.pathname,
        state: {
          scrollPosition: window.scrollY,
          modalOpen: isModalOpen,
          verseId: selectedVerse?.reference
        }
      });
      
      navigate(`/bible/${book}/${chapter}`, {
        state: { verse, version: 'NIV' }
      });
    }
  };

  return (
    <ProFeatureGuard feature="Mood Scriptures">
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">Mood Scriptures</h1>
            <HelpButton pageName="mood-scriptures" className="ml-2" />
          </div>
        </div>
        <div className="min-h-screen p-4 sm:p-6 md:p-8">
          {/* Mood categories */}
          <div className="space-y-8" data-tutorial="mood-categories">
            {moodCategories.map((category) => (
              <div key={category.category} className="space-y-4">
                <h2 className="text-xl font-semibold text-white/90">{category.category}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-tutorial="mood-selection">
                  {category.moods.map((mood) => {
                    const Icon = mood.icon;
                    const isSelected = selectedMood === mood.id;
                    return (
                      <button
                        key={mood.id}
                        onClick={() => handleMoodSelect(mood.id)}
                        className={`flex flex-col items-center justify-center p-4 rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-gold-500/20 border-gold-500 text-gold-400'
                            : 'bg-slate-800/50 border-white/10 text-white/80 hover:bg-slate-800 hover:border-white/20'
                        }`}
                      >
                        <Icon className="w-6 h-6 mb-2" />
                        <span className="text-sm text-center">{mood.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Response Modal */}
          <MoodResponseModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            mood={selectedMood}
            verses={verses}
            loading={loading}
            error={error}
          />

          {/* AI Assistant Section */}
          {showAI && (
            <div ref={aiRef} className="mt-8">
              <InlineAIAssistant
                initialQuestion={aiQuestion || ''}
                onClose={() => setShowAI(false)}
              />
            </div>
          )}

          {/* Note Editor */}
          {showNoteEditor && verseForNote && (
            <NoteEditor
              verseId={verseForNote.id}
              reference={verseForNote.reference}
              onClose={() => setShowNoteEditor(false)}
            />
          )}
        </div>
      </div>
    </ProFeatureGuard>
  );
}